import React, { useCallback } from 'react'
import classNames from 'classnames'
import CarouselNav from '@/atoms/CarouselNav'
import { Image } from '@/atoms/Image'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { CaptionLG, HeadingMD, TitleMD } from '@/atoms/Text'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useDimensions } from '@/utils/ScreenUtils'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import useCarousel from '@/utils/useCarousel'
import useWindowSize from '@/utils/useWindowSize'
import { TorchSubmitButton } from '../TorchSubmitButton'
import { AngelTorchVideoModal } from './AngelTorchVideoModal'

export const TorchWatchSection: React.FC = () => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [torchScrollerRef, { canScrollBack, canScrollForward, scrollBack, scrollForward }] =
    useCarousel<HTMLDivElement>()
  const { track } = useSafeAnalytics()
  const { width: containerWidth } = useDimensions(containerRef)
  const { innerWidth } = useWindowSize()
  const { t } = useTranslate('create')
  const leftPadding = innerWidth > 1680 && containerWidth && containerWidth > 1440 && (containerWidth - 1440) / 2

  const handleSubmitClicked = useCallback(() => {
    track('Guild Submit Project Clicked')
  }, [track])

  return (
    <section
      className="bg-core-gray-900 bg-cover bg-center bg-no-repeat py-14 text-core-gray-950 md:py-[120px] se:py-4"
      ref={containerRef}
      style={{
        backgroundImage:
          'linear-gradient(180deg, #F5F5F4 0%, #C3C1C0 100%), url(https://images.angelstudios.com/image/upload/v1717178054/angel-studios/create/23d15d7e3a4d9fdc60ac8a81bb38a630_1.png)',
      }}
    >
      <div className="relative w-full">
        <div className="max-w-[1440px] px-4 md:px-12 lg:px-[120px] se:mt-8 3lg:mx-auto 3lg:px-0">
          <HeadingMD weight="bold" className="md:photon-heading-xl lg:photon-display text-center">
            {t('torchesThatPassedGuildTitle', 'Torches That Passed the Guild')}
          </HeadingMD>
        </div>
        <div
          className="relative flex w-full snap-x snap-mandatory flex-row gap-8 overflow-scroll px-4 scrollbar-none sm:flex-row md:px-12 lg:px-[120px] 3lg:px-0"
          ref={torchScrollerRef}
          style={{ paddingLeft: leftPadding ?? 0 }}
        >
          <TorchCard
            title="Cabrini"
            description="Completed Movie Torch"
            backgroundImageUrl="v1702511354/studio-app/catalog/0bb2bf06-5bfb-41f3-9cfc-fda092931bb9"
            torchUrl="https://media.angelstudios.com/copied-content/masters/a670cc4d-d3f4-4785-ac16-a7be92f68af6.m3u8"
          />
          <TorchCard
            title="Fellowship"
            description="Unfinished Movie Torch"
            backgroundImageUrl="https://images.angelstudios.com/image/upload/v1717438313/angel-studios/create/Fellowship.png"
            torchUrl="https://stream.mux.com/B02026eXeMCBCK94dRTTAca7S01Hpjx1llaOZjoTDZcBuA.m3u8"
          />
          <TorchCard
            title="A Week Away"
            description="Series Torch"
            backgroundImageUrl="/v1717426729/angel-studios/create/Screenshot_2024-06-03_at_8.58.36_AM.png"
            torchUrl="https://stream.mux.com/btmOwBBvS3bscMw02bwdFk5LVWZVtS01MYKwIARJCRV5A.m3u8"
          />
          <TorchCard
            title="DAVID"
            description="Kids Torch"
            backgroundImageUrl="v1652468593/angel-app/for-your-consideration/David_16x9_KeyArt_en"
            torchUrl="https://media.angelstudios.com/copied-from-old-account/FYC/David/torch/david_torch_fyc.m3u8"
          />
          <TorchCard
            title="Young DAVID"
            description="Animatic Kids Torch"
            backgroundImageUrl="v1704866747/angel-app/guild/torches/YD_Ep_4"
            torchUrl="https://stream.mux.com/PhmE02HAACBAPY73KZDniBy5pYtz8RNJUsgAGrI3BPyU.m3u8"
          />
        </div>
        <CarouselNav
          onClick={scrollBack}
          active={canScrollBack}
          orientation="horizontal"
          direction="back"
          className={classNames('ml-4', { 'opacity-75 hover:opacity-100': canScrollBack })}
          theme="dark"
        />
        <CarouselNav
          onClick={scrollForward}
          active={canScrollForward}
          orientation="horizontal"
          direction="forward"
          className={classNames('mr-4', { 'opacity-75 hover:opacity-100': canScrollForward })}
          theme="dark"
        />
      </div>

      <div className="flex flex-col items-center gap-4">
        <TorchSubmitButton handleSubmitClicked={handleSubmitClicked} isDark={false} />
        <CaptionLG weight="normal">{t('applicationFee', '$250 application fee')}</CaptionLG>
      </div>
    </section>
  )
}

interface TorchCardProps {
  title?: string
  description?: string
  backgroundImageUrl?: string
  torchUrl: string
}

const TorchCard: React.FC<TorchCardProps> = ({ title, description, backgroundImageUrl, torchUrl }) => {
  const { track } = useSafeAnalytics()
  const [showModal, setShowModal] = React.useState(false)

  const handleModalClick = useCallback(() => {
    setShowModal(true)
    track('Watch For Free Card Clicked')
  }, [track])

  const handleClose = () => {
    setShowModal(false)
  }

  return (
    <div
      onClick={handleModalClick}
      className="flex cursor-pointer snap-center flex-col justify-between py-6 text-core-gray-950 lg:py-8 se:py-3"
    >
      <div className="relative overflow-hidden rounded-2xl">
        <Image
          className="flex max-h-[284px] min-h-[180px] min-w-[320px] lg:min-h-[284px] lg:min-w-[569px] se:min-h-[145px] se:min-w-[240px]"
          width={240}
          height={135}
          src={getCloudinaryImageUrl({ path: backgroundImageUrl ?? '' })}
          alt={title}
        />
        <PlayButtonThumbnailOverlay />
      </div>
      <div className="mt-[17px] flex flex-col items-start gap-1 text-left">
        <TitleMD className="md:photon-title-lg" weight="bold">
          {title}
        </TitleMD>
        <CaptionLG className="md:photon-paragraph-lg" color="core-gray-800" weight="medium">
          {description}
        </CaptionLG>
      </div>
      <AngelTorchVideoModal showModal={showModal} torchVideoUrl={torchUrl} handleClose={handleClose} />
    </div>
  )
}
